import { useState, useRef, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Search } from 'lucide-react';
import { useDebounce } from '@/shared/hooks/useDebounce.ts';

export interface TenantOption {
  id: string;
  nome: string;
  slug: string;
}

interface Props {
  tenants: TenantOption[];
  selected: string[];
  selectAll: boolean;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}

const ROW_HEIGHT = 36;
const VIRTUALIZE_THRESHOLD = 50;

export function TenantMultiSelect({
  tenants,
  selected,
  selectAll,
  onToggle,
  onSelectAll,
  onClearAll,
}: Props) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 200);
  const parentRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!debouncedSearch) return tenants;
    const lower = debouncedSearch.toLowerCase();
    return tenants.filter(
      (t) => t.nome.toLowerCase().includes(lower) || t.slug.toLowerCase().includes(lower),
    );
  }, [tenants, debouncedSearch]);

  const useVirtual = filtered.length > VIRTUALIZE_THRESHOLD;

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    enabled: useVirtual,
  });

  const isAllSelected = selectAll || (tenants.length > 0 && selected.length === tenants.length);
  const isIndeterminate = !isAllSelected && selected.length > 0;

  const handleSelectAllClick = useCallback(() => {
    if (isAllSelected) {
      onClearAll();
    } else {
      onSelectAll();
    }
  }, [isAllSelected, onClearAll, onSelectAll]);

  const selectedCount = selectAll ? tenants.length : selected.length;

  const renderRow = (tenant: TenantOption) => {
    const checked = selectAll || selected.includes(tenant.id);
    return (
      <label
        key={tenant.id}
        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(tenant.id)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span className="flex-1 truncate">{tenant.nome}</span>
        <span className="font-mono text-xs text-gray-400">{tenant.slug}</span>
      </label>
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Tenants Afetados</span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {selectedCount} de {tenants.length} selecionado{selectedCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Busca */}
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar tenant..."
          className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
        />
      </div>

      {/* Selecionar todos */}
      <label className="flex cursor-pointer items-center gap-2 rounded border-b border-gray-200 px-2 pb-2 text-sm font-medium dark:border-gray-700">
        <input
          type="checkbox"
          ref={(el) => {
            if (el) el.indeterminate = isIndeterminate;
          }}
          checked={isAllSelected}
          onChange={handleSelectAllClick}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        Selecionar todos
      </label>

      {/* Lista */}
      <div
        ref={parentRef}
        className="max-h-52 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700"
        style={useVirtual ? { height: Math.min(filtered.length * ROW_HEIGHT, 208) } : undefined}
      >
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-gray-400">
            Nenhum tenant encontrado.
          </div>
        ) : useVirtual ? (
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const tenant = filtered[virtualRow.index];
              return (
                <div
                  key={tenant.id}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {renderRow(tenant)}
                </div>
              );
            })}
          </div>
        ) : (
          filtered.map(renderRow)
        )}
      </div>
    </div>
  );
}
